import { useState } from 'react';
import { Button, Loader, Paper, Stack, TextInput, Text, Switch, Group, Select } from '@mantine/core';
import { apiClient } from '@shared/utils/api-client';
import '@shared/styles/page.css';
import './TbankTestPage.css';

interface SbpBank {
    bankId: string;
    name?: string;
    logoUrl?: string;
}

interface GetQrBankListResponse {
    success: boolean;
    banks?: SbpBank[];
    errorCode?: string;
    message?: string;
}

interface GetCustomerResponse {
    success: boolean;
    errorCode?: string;
    message?: string;
    details?: string;
    terminalKey?: string;
    customerKey?: string;
    email?: string;
    phone?: string;
}

interface GetCardListResponse {
    success: boolean;
    errorCode?: string;
    message?: string;
    cards: Array<{ cardId: string; pan: string; status: string; rebillId?: string; expDate?: string }>;
}

interface BinIssuerResponse {
    bin: string;
    issuer: string | null;
}

export default function TbankTestPage() {
    const [customerKey, setCustomerKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingCards, setLoadingCards] = useState(false);
    const [result, setResult] = useState<GetCustomerResponse | null>(null);
    const [cardsResult, setCardsResult] = useState<GetCardListResponse | null>(null);
    const [savedCardOnly, setSavedCardOnly] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [loadingBanks, setLoadingBanks] = useState(false);
    const [banksResult, setBanksResult] = useState<GetQrBankListResponse | null>(null);
    const [banksDevice, setBanksDevice] = useState<'mobile' | 'desktop'>('mobile');
    const [banksOs, setBanksOs] = useState('Android');

    const [testBin, setTestBin] = useState('');
    const [loadingBinIssuer, setLoadingBinIssuer] = useState(false);
    const [binIssuerResult, setBinIssuerResult] = useState<BinIssuerResponse | null>(null);

    const handleFetchCustomer = async () => {
        if (!customerKey.trim()) return;
        setLoading(true);
        setError(null);
        setResult(null);
        setCardsResult(null);
        try {
            const data = await apiClient.get<GetCustomerResponse>(
                `/tbank-acquiring/customer/${encodeURIComponent(customerKey.trim())}`,
            );
            setResult(data);
        } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'message' in err
                ? String((err as { message?: unknown }).message)
                : 'Ошибка запроса';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleFetchCards = async () => {
        if (!customerKey.trim()) return;
        setLoadingCards(true);
        setError(null);
        setCardsResult(null);
        try {
            const url = `/tbank-acquiring/cards/customer/${encodeURIComponent(customerKey.trim())}?savedCard=${savedCardOnly}`;
            const data = await apiClient.get<GetCardListResponse>(url);
            setCardsResult(data);
        } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'message' in err
                ? String((err as { message?: unknown }).message)
                : 'Ошибка запроса';
            setError(msg);
        } finally {
            setLoadingCards(false);
        }
    };

    const handleFetchBanks = async () => {
        setLoadingBanks(true);
        setError(null);
        setBanksResult(null);
        try {
            const params = new URLSearchParams({ device: banksDevice, os: banksOs });
            const data = await apiClient.get<GetQrBankListResponse>(
                `/tbank-acquiring/sbp-banks?${params.toString()}`,
            );
            setBanksResult(data);
        } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'message' in err
                ? String((err as { message?: unknown }).message)
                : 'Ошибка запроса';
            setError(msg);
        } finally {
            setLoadingBanks(false);
        }
    };

    const handleTestBinIssuer = async () => {
        if (!testBin.trim()) return;
        setLoadingBinIssuer(true);
        setError(null);
        setBinIssuerResult(null);
        try {
            const params = new URLSearchParams({ bin: testBin.trim() });
            const data = await apiClient.get<BinIssuerResponse>(
                `/tbank-acquiring/bin-issuer?${params.toString()}`,
            );
            setBinIssuerResult(data);
        } catch (err: unknown) {
            const msg =
                err && typeof err === 'object' && 'message' in err
                    ? String((err as { message?: unknown }).message)
                    : 'Ошибка запроса';
            setError(msg);
        } finally {
            setLoadingBinIssuer(false);
        }
    };

    return (
        <div className="page">
            <h1 className="page-title">T-Bank — тестовая страница</h1>
            <p className="page-subtitle">
                Получение данных клиента и списка карт из T-Bank по CustomerKey (userId).
            </p>
            <Text size="sm" c="dimmed" mb="md">
                Карты сохраняются только при оплате картой (ввод реквизитов). Оплата через СБП не сохраняет карты. По умолчанию сохранение карт отключено: для DEMO — написать acq_help@tbank.ru, для боевого — менеджеру T-Bank.
            </Text>

            <Paper p="md" withBorder className="tbank-test-form">
                <Stack gap="md">
                    <TextInput
                        label="CustomerKey (userId)"
                        placeholder="Например: 2"
                        value={customerKey}
                        onChange={(e) => setCustomerKey(e.target.value)}
                        disabled={loading || loadingCards}
                    />
                    <Group>
                        <Button onClick={handleFetchCustomer} loading={loading} disabled={!customerKey.trim()}>
                            GetCustomer
                        </Button>
                        <Button
                            variant="light"
                            onClick={handleFetchCards}
                            loading={loadingCards}
                            disabled={!customerKey.trim()}
                        >
                            GetCardList
                        </Button>
                    </Group>
                    <Switch
                        label="SavedCard: true (только карты для оплаты в один клик)"
                        checked={savedCardOnly}
                        onChange={(e) => setSavedCardOnly(e.currentTarget.checked)}
                    />
                </Stack>
            </Paper>

            <Paper p="md" withBorder className="tbank-test-form" style={{ marginTop: '1.5rem' }}>
                <Text size="sm" fw={600} mb="md">Список банков СБП (GetQrBankList)</Text>
                <Stack gap="md">
                    <Select
                        label="Устройство"
                        data={[
                            { value: 'mobile', label: 'mobile' },
                            { value: 'desktop', label: 'desktop' },
                        ]}
                        value={banksDevice}
                        onChange={(v) => v && setBanksDevice(v as 'mobile' | 'desktop')}
                        disabled={loadingBanks}
                    />
                    <Select
                        label="ОС"
                        data={[
                            { value: 'Android', label: 'Android' },
                            { value: 'iOS', label: 'iOS' },
                            { value: 'Windows', label: 'Windows' },
                            { value: 'macOS', label: 'macOS' },
                        ]}
                        value={banksOs}
                        onChange={(v) => v && setBanksOs(v)}
                        disabled={loadingBanks}
                    />
                    <Button onClick={handleFetchBanks} loading={loadingBanks}>
                        Получить банки
                    </Button>
                </Stack>
            </Paper>

            <Paper p="md" withBorder className="tbank-test-form" style={{ marginTop: '1.5rem' }}>
                <Text size="sm" fw={600} mb="md">
                    Проверка BIN (binlist + кэш в БД)
                </Text>
                <Stack gap="md">
                    <TextInput
                        label="BIN (первые 6–9 цифр карты)"
                        placeholder="Например: 430000"
                        value={testBin}
                        onChange={(e) => setTestBin(e.target.value)}
                        disabled={loadingBinIssuer}
                    />
                    <Button onClick={handleTestBinIssuer} loading={loadingBinIssuer} disabled={!testBin.trim()}>
                        Проверить банк по BIN
                    </Button>
                    {binIssuerResult && (
                        <Stack gap={4}>
                            <Text size="sm">
                                BIN: <b>{binIssuerResult.bin}</b>
                            </Text>
                            <Text size="sm">
                                Банк:{' '}
                                <b>{binIssuerResult.issuer ? binIssuerResult.issuer : 'не найден / не определён'}</b>
                            </Text>
                        </Stack>
                    )}
                </Stack>
            </Paper>

            {loading && (
                <Paper p="md" withBorder className="tbank-test-result">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Загрузка...</Text>
                </Paper>
            )}

            {error && (
                <Paper p="md" withBorder className="tbank-test-result tbank-test-error">
                    <Text size="sm" c="red">{error}</Text>
                </Paper>
            )}

            {result && !loading && (
                <Paper p="md" withBorder className="tbank-test-result">
                    <Text size="sm" fw={600} mb="xs">Результат GetCustomer:</Text>
                    <pre className="tbank-test-pre">{JSON.stringify(result, null, 2)}</pre>
                    {result.success && (
                        <Stack gap="xs" mt="md">
                            {result.email && <Text size="sm">Email: {result.email}</Text>}
                            {result.phone && <Text size="sm">Phone: {result.phone}</Text>}
                            {result.customerKey && <Text size="sm">CustomerKey: {result.customerKey}</Text>}
                        </Stack>
                    )}
                </Paper>
            )}

            {cardsResult && !loadingCards && (
                <Paper p="md" withBorder className="tbank-test-result">
                    <Text size="sm" fw={600} mb="xs">Результат GetCardList (SavedCard={String(savedCardOnly)}):</Text>
                    {!cardsResult.success && (cardsResult.errorCode || cardsResult.message) && (
                        <Text size="sm" c="red" mb="xs" fw={500}>
                            T-Bank ответил success: false — ErrorCode: {cardsResult.errorCode ?? '—'}, Message: {cardsResult.message ?? '—'}
                        </Text>
                    )}
                    <pre className="tbank-test-pre">{JSON.stringify(cardsResult, null, 2)}</pre>
                    {cardsResult.cards.length === 0 && (
                        <Stack gap="xs" mt="xs">
                            {(cardsResult.message || cardsResult.errorCode) && cardsResult.success && (
                                <Text size="sm" c="dimmed">
                                    T-Bank: {[cardsResult.errorCode, cardsResult.message].filter(Boolean).join(' — ')}
                                </Text>
                            )}
                            <Text size="sm" c="orange">
                                Карт нет. Возможные причины: оплата через СБП (карты не сохраняются); сохранение карт не включено для терминала (для DEMO — acq_help@tbank.ru); попробуйте SavedCard=false.
                            </Text>
                            <Text size="xs" c="dimmed">
                                Для CC-покупки нужны Recurrent=Y и CustomerKey в Init — у нас они передаются. После успешной оплаты картой T-Bank сохраняет карту и возвращает RebillId в уведомлении. GetCardList должен вернуть сохранённые карты.
                            </Text>
                        </Stack>
                    )}
                </Paper>
            )}

            {banksResult && !loadingBanks && (
                <Paper p="md" withBorder className="tbank-test-result">
                    <Text size="sm" fw={600} mb="xs">Результат GetQrBankList (device={banksDevice}, os={banksOs}):</Text>
                    <pre className="tbank-test-pre">{JSON.stringify(banksResult, null, 2)}</pre>
                    {banksResult.banks && banksResult.banks.length > 0 && (
                        <Stack gap="xs" mt="md">
                            <Text size="sm" fw={500}>Банков: {banksResult.banks.length}</Text>
                            <Stack gap={4}>
                                {banksResult.banks.slice(0, 20).map((b) => (
                                    <div key={b.bankId}>
                                        <Text size="sm" span>{b.name ?? b.bankId}{b.logoUrl ? ' [logo]' : ''}</Text>
                                        <Text size="xs" c="dimmed" ml="xs" span>({b.bankId})</Text>
                                    </div>
                                ))}
                                {banksResult.banks.length > 20 && (
                                    <Text size="xs" c="dimmed">… и ещё {banksResult.banks.length - 20}</Text>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    {banksResult.success && (!banksResult.banks || banksResult.banks.length === 0) && (
                        <Text size="sm" c="orange" mt="md">
                            Список пустой. Часто так бывает у тестового (DEMO) терминала: СБП может быть отключён. Включите СБП в личном кабинете T-Bank (Приём оплаты → Готовая форма → Настроить). Для DEMO при необходимости пишите в acq_help@tbank.ru.
                        </Text>
                    )}
                </Paper>
            )}
        </div>
    );
}
